import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import './RequestCategoryModal.css';

function RequestCategoryModal({ isOpen, onClose, onSuccess, selectedOtherDuringRegistration = false, deferSubmission = false }) {
  const [formData, setFormData] = useState({
    categoryName: '',
    description: '',
    reason: ''
  });
  const [similarCategories, setSimilarCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Search for similar categories as user types
  useEffect(() => {
    if (formData.categoryName.trim().length >= 3) {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }

      const timeout = setTimeout(async () => {
        try {
          const token = localStorage.getItem('accessToken');
          const headers = token 
            ? { 'Authorization': `Bearer ${token}` }
            : {};

          const response = await axios.get(
            `/api/category-requests/similar?name=${encodeURIComponent(formData.categoryName)}`,
            { headers }
          );
          setSimilarCategories(response.data.similarCategories || []);
        } catch (error) {
          console.error('Error searching similar categories:', error);
        }
      }, 500);

      setSearchTimeout(timeout);
    } else {
      setSimilarCategories([]);
    }

    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [formData.categoryName]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    // Check if user is authenticated
    const token = localStorage.getItem('accessToken');
    
    // If no token (during registration), just return the data to parent
    if (!token || deferSubmission) {
      console.log('📝 Deferring category request submission until after registration');
      onSuccess(formData); // Pass the form data to parent
      setFormData({ categoryName: '', description: '', reason: '' });
      setSimilarCategories([]);
      setIsLoading(false);
      return;
    }

    // Otherwise, submit immediately (user is logged in)
    try {
      const response = await axios.post('/api/category-requests', {
        ...formData,
        selectedOtherDuringRegistration
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      onSuccess(formData);
      setFormData({ categoryName: '', description: '', reason: '' });
      setSimilarCategories([]);
    } catch (error) {
      console.error('Error submitting category request:', error);
      const data = error.response?.data;
      if (data?.errors) {
        const errorMap = {};
        data.errors.forEach(err => {
          errorMap[err.field] = err.message;
        });
        setErrors(errorMap);
      } else {
        setErrors({ general: data?.message || 'Failed to submit request. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Request New Identity Category</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="request-category-form">
          {errors.general && (
            <div className="error-message general-error">{errors.general}</div>
          )}

          <div className="form-group">
            <label htmlFor="categoryName">
              Category Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="categoryName"
              name="categoryName"
              value={formData.categoryName}
              onChange={handleChange}
              maxLength={50}
              placeholder="e.g., Disability, LGBTQ+, Profession"
              className={errors.categoryName ? 'error' : ''}
            />
            <div className="char-count">{formData.categoryName.length}/50</div>
            {errors.categoryName && (
              <div className="error-message">{errors.categoryName}</div>
            )}
          </div>

          {similarCategories.length > 0 && (
            <div className="similar-categories">
              <h4>Similar categories found:</h4>
              <div className="similar-list">
                {similarCategories.map((cat, index) => (
                  <div key={index} className="similar-item">
                    <div className="similar-header">
                      {cat.icon && <span className="category-icon">{cat.icon}</span>}
                      <span className="category-name">{cat.displayName || cat.name}</span>
                      <span className="similarity-badge">
                        {Math.round(cat.similarityScore * 100)}% match
                      </span>
                      {cat.type === 'pending' && (
                        <span className="pending-badge">Pending</span>
                      )}
                    </div>
                    <p className="category-description">{cat.description}</p>
                  </div>
                ))}
              </div>
              <p className="similar-note">
                If one of these matches what you're looking for, you don't need to submit a new request.
              </p>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="description">
              Description <span className="required">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              maxLength={200}
              rows={3}
              placeholder="Brief description of this identity category"
              className={errors.description ? 'error' : ''}
            />
            <div className="char-count">{formData.description.length}/200</div>
            {errors.description && (
              <div className="error-message">{errors.description}</div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="reason">
              Why is this category needed? <span className="required">*</span>
            </label>
            <textarea
              id="reason"
              name="reason"
              value={formData.reason}
              onChange={handleChange}
              maxLength={500}
              rows={4}
              placeholder="Explain why this category would be valuable for the Belong community"
              className={errors.reason ? 'error' : ''}
            />
            <div className="char-count">{formData.reason.length}/500</div>
            {errors.reason && (
              <div className="error-message">{errors.reason}</div>
            )}
          </div>

          <div className="info-box">
            <p>
              <strong>Note:</strong> Category requests are typically reviewed within 3-5 days.
              You'll receive a notification when your request is reviewed.
            </p>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {isLoading ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  // Use portal to render modal outside of parent form
  return ReactDOM.createPortal(
    modalContent,
    document.body
  );
}

export default RequestCategoryModal;
